import React, { useState } from "react";
import { 
  BookOpen, Clock, Calendar, CheckCircle2, Copy, Check, ShieldAlert, 
  Sparkles, ArrowRight, Lightbulb, Send, MessageSquare
} from "lucide-react";
import { getGuideBySlug, GuideArticle } from "../../utils/guideRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";
import { HowToAskIfABusinessIsHiringGuide } from "./HowToAskIfABusinessIsHiringGuide";
import { 
  SeoSearchActionModule, 
  SeoWhyPageExistsSection, 
  SeoExampleResultsPreview, 
  SampleBusinessCard 
} from "./SeoUniversalPageEngine";

interface GuidePageProps {
  guideSlug: string;
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function GuidePage({ guideSlug, onNavigate, onLogin = () => {}, currentUser }: GuidePageProps) {
  if (guideSlug === "how-to-ask-if-a-business-is-hiring") {
    return <HowToAskIfABusinessIsHiringGuide onNavigate={onNavigate} onLogin={onLogin} />;
  }

  const guide: GuideArticle | null = getGuideBySlug(guideSlug);
  const [copiedScript, setCopiedScript] = useState(false);

  if (!guide) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-bold text-slate-800 mb-2">Guide Article Not Found</h1>
        <p className="text-sm text-slate-600 mb-4">The career guide you requested does not exist or has been relocated.</p>
        <button
          onClick={() => onNavigate("/guides")}
          className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-medium text-xs hover:bg-indigo-700 transition"
        >
          View All Career Guides
        </button>
      </div>
    );
  }

  const currentRoute = `/guides/${guide.slug}`;

  useSeoMetadata({
    title: guide.title,
    description: guide.metaDescription,
    canonicalUrl: `https://getbiznearyou.com${currentRoute}`,
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": guide.h1,
      "description": guide.metaDescription,
      "datePublished": guide.publishedDate,
      "dateModified": guide.lastUpdatedDate,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://getbiznearyou.com${currentRoute}`
      },
      "publisher": {
        "@type": "Organization",
        "name": "BizNearYou",
        "logo": {
          "@type": "ImageObject",
          "url": "https://getbiznearyou.com/favicon-512.png"
        }
      }
    }
  });

  const handleCopyScript = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedScript(true);
    setTimeout(() => setCopiedScript(false), 2500);
  };

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="flex-grow max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs
          items={[
            { label: "Guides", path: "/guides" },
            { label: guide.h1, path: currentRoute }
          ]}
          onNavigate={onNavigate}
        />

        {/* Article Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700">
              {guide.category}
            </span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{guide.estimatedReadTime}</span>
            </span>
            <span className="text-xs text-slate-400">•</span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>Updated {guide.lastUpdatedDate}</span>
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight mb-4">
            {guide.h1}
          </h1>
        </header>

        {/* Search & Action Box inside Guide */}
        <div className="mb-8">
          <SeoSearchActionModule
            defaultLocation=""
            defaultKeyword=""
            onNavigate={onNavigate}
          />
        </div>

        {/* Direct Answer Box (SEO & User First) */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-8 shadow-sm">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-2">Direct Summary Answer</h2>
              <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-medium">
                {guide.directAnswer}
              </p>
            </div>
          </div>
        </div>

        {/* Key Takeaways */}
        {guide.keyTakeaways && guide.keyTakeaways.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8 shadow-sm">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Key Action Points</span>
            </h2>
            <ul className="space-y-2">
              {guide.keyTakeaways.map((point, i) => (
                <li key={i} className="text-xs text-slate-700 flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 mt-1.5 flex-shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Main Content Sections */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 mb-8 space-y-8 shadow-sm">
          {guide.contentSections.map((sec, i) => (
            <section key={i} className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">{sec.heading}</h2>
              {sec.paragraphs.map((para, pIdx) => (
                <p key={pIdx} className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  {para}
                </p>
              ))}

              {sec.bulletPoints && (
                <ul className="space-y-2 pl-2">
                  {sec.bulletPoints.map((bp, bpIdx) => (
                    <li key={bpIdx} className="text-xs sm:text-sm text-slate-700 flex items-start gap-2">
                      <span className="font-bold text-indigo-600">•</span>
                      <span>{bp}</span>
                    </li>
                  ))}
                </ul>
              )}

              {sec.proTip && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-3 mt-3">
                  <Lightbulb className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold block mb-0.5">Pro Tip:</span>
                    <span>{sec.proTip}</span>
                  </div>
                </div>
              )}
            </section>
          ))}

          {/* Sample Script Box */}
          {guide.sampleScript && (
            <div className="mt-8 pt-8 border-t border-slate-200">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-indigo-600" />
                  <span>{guide.sampleScript.label}</span>
                </h2>
                <button
                  onClick={() => handleCopyScript(guide.sampleScript!.text)}
                  className="px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold text-xs transition flex items-center gap-1.5 cursor-pointer"
                >
                  {copiedScript ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy Script</span>
                    </>
                  )}
                </button>
              </div>

              <div className="bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-100 p-5 rounded-xl font-mono text-xs leading-relaxed overflow-x-auto whitespace-pre-wrap border border-slate-200 dark:border-slate-800">
                {guide.sampleScript.text}
              </div>
            </div>
          )}

          {/* Safety Warning if applicable */}
          {guide.safetyWarning && (
            <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-xs text-rose-900 dark:text-rose-200 flex items-start gap-3 mt-6">
              <ShieldAlert className="w-4 h-4 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-bold block mb-0.5">Employment Safety Note:</span>
                <span>{guide.safetyWarning}</span>
              </div>
            </div>
          )}
        </div>

        {/* CTA Banner */}
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-2xl p-6 sm:p-8 text-center mb-8 shadow-sm border border-slate-200 dark:border-slate-800">
          <h2 className="text-lg sm:text-xl font-bold mb-2 text-slate-900 dark:text-white">Put This Guide Into Action with BizNearYou</h2>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mb-5">
            Discover verified local small businesses, medical practices, and trade contractors near you.
          </p>
          <a
            href="/find-jobs"
            onClick={(e) => handleLinkClick(e, "/find-jobs")}
            className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs transition inline-block cursor-pointer shadow-xs"
          >
            Start Local Employer Search
          </a>
        </div>

        <SeoRelatedLinks currentRoute={currentRoute} onNavigate={onNavigate} />
      </main>
    </div>
  );
}
